/**
 * The "By job" panel, extracted from the recruiter dashboard so the new
 * Hiring Manager dashboard can render the identical table scoped to fewer
 * jobs, rather than a second, forked copy of the same markup. Same columns
 * make sense in both places: it's the same shape of data either way.
 *
 * Owns its own pagination -- a caller just hands it whatever `jobs`/`byJob`
 * it already fetched (both dashboards' data comes from the same
 * useDashboardData fan-out; the backend already scopes jobs per role, so a
 * Hiring Manager's `jobs` array is already just their own).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { Pagination } from 'antd';
import CaptionInfo from '../../../../components/common/caption-info';
import { summariseByJob, JobRow, CandidateRow } from '../../utils/aggregate';
import styles from './index.module.css';

/** Matches job-list's own pageSize -- same shape of data, same page size. */
const BY_JOB_PAGE_SIZE = isMobile ? 6 : 10;

export interface ByJobTableProps {
  jobs: JobRow[];
  byJob: Array<{ jobId: number; rows: CandidateRow[] }>;
}

export const ByJobTable = ({ jobs, byJob }: ByJobTableProps) => {
  const [page, setPage] = useState(1);
  const jobBreakdown = summariseByJob(jobs, byJob);

  // A reload can change which jobs are in the sample -- start back at page 1
  // rather than risk landing on a now out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [jobBreakdown.length]);

  const rows = jobBreakdown.slice(
    (page - 1) * BY_JOB_PAGE_SIZE,
    page * BY_JOB_PAGE_SIZE,
  );

  return (
    <section className={`${styles.panel} ${styles.panelTwoCol}`}>
      <h2 className={styles.panelTitle}>
        By job
        <CaptionInfo
          label="About the by-job breakdown"
          text="A job that failed to load is left out, not shown as zero."
        />
      </h2>
      {jobBreakdown.length ? (
        <div className={styles.tableScroll}>
          <table className={styles.jobTable}>
            <thead>
              <tr>
                <th scope="col">Job</th>
                <th scope="col" className={styles.num}>
                  Candidates
                </th>
                <th scope="col" className={styles.num}>
                  In pipeline
                </th>
                <th scope="col" className={styles.num}>
                  Awaiting you
                </th>
                <th scope="col" className={styles.num}>
                  Hired
                </th>
                <th scope="col" className={styles.num}>
                  Best fit
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.jobId}>
                  <td>
                    <Link to={`/jobs/${r.jobId}`} className={styles.jobLink}>
                      {r.title}
                    </Link>
                    {r.status === 'CLOSED' && (
                      <span className={styles.closedFlag}>closed</span>
                    )}
                  </td>
                  <td className={styles.num}>{r.total}</td>
                  <td className={styles.num}>{r.inPipeline}</td>
                  <td className={styles.num}>
                    {r.awaitingReview > 0 ? (
                      <strong className={styles.awaiting}>
                        {r.awaitingReview}
                      </strong>
                    ) : (
                      r.awaitingReview
                    )}
                  </td>
                  <td className={styles.num}>{r.hired}</td>
                  {/* An em dash, not a zero: no score is not a score of zero,
                      and a 0 here would understate the job. */}
                  <td className={styles.num}>
                    {r.bestScore === null ? (
                      <span className={styles.noScore}>&mdash;</span>
                    ) : (
                      r.bestScore
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.empty}>
          No job loaded a candidate list, so there is nothing to break down.
        </p>
      )}
      {jobBreakdown.length > BY_JOB_PAGE_SIZE && (
        <Pagination
          current={page}
          pageSize={BY_JOB_PAGE_SIZE}
          total={jobBreakdown.length}
          onChange={setPage}
          showSizeChanger={false}
          size="small"
        />
      )}
    </section>
  );
};

export default ByJobTable;
